import { initComponent } from './core/init-component';

/**
 * Horizontal filters in TWO ROWS:
 *   Row 1: filter names (FIXED).
 *   Row 2: the open filter's options, underneath (in `.ls-facets-row2`).
 *
 * The native markup nests each filter's options panel inside its name's <details>. To keep the
 * names row from moving, we move the open filter's panel into a Row 2 container.
 *
 * Challenge: when an option is selected, the filters re-render (morph) which returns the panel
 * to its <details> and removes our Row 2. That is why we reconcile state on every DOM change
 * (MutationObserver), idempotently and with an anti-recursion guard, so the open filter STAYS
 * open and in place after filtering.
 */
initComponent('.facets--horizontal', (root) => {
  let openId: string | null = null;
  let busy = false;
  const bound = new WeakSet<HTMLElement>();

  const ensureRow2 = (): HTMLElement | null => {
    const wrapper = root.querySelector<HTMLElement>('.facets__filters-wrapper');
    if (!wrapper) return null;
    let row2 = root.querySelector<HTMLElement>('.ls-facets-row2');
    if (!row2) {
      row2 = document.createElement('div');
      row2.className = 'ls-facets-row2';
      wrapper.insertAdjacentElement('afterend', row2);
    }
    return row2;
  };

  const bindToggles = () => {
    root.querySelectorAll<HTMLDetailsElement>('details.facets__panel').forEach((details) => {
      if (bound.has(details)) return;
      bound.add(details);
      details.addEventListener('toggle', () => {
        if (busy) return; // programmatic changes: ignore
        if (details.open) openId = details.id;
        else if (openId === details.id) openId = null;
        reconcile();
      });
    });
  };

  const reconcile = () => {
    if (busy) return;
    busy = true;
    try {
      bindToggles();

      // "Clear all" must sit to the right of the last filter, inside the names row.
      // The native markup leaves it as a sibling below; we move it to the end of overflow-list.
      const overflowList = root.querySelector<HTMLElement>('.facets__overflow-list');
      const clearAll = root.querySelector<HTMLElement>('.facets-horizontal-remove');
      if (overflowList && clearAll && clearAll.parentElement !== overflowList) {
        overflowList.appendChild(clearAll);
      }

      const row2 = ensureRow2();
      if (!row2) return;

      root.querySelectorAll<HTMLDetailsElement>('details.facets__panel').forEach((details) => {
        const shouldOpen = details.id === openId;
        if (details.open !== shouldOpen) details.open = shouldOpen;
        if (shouldOpen) {
          const content = details.querySelector<HTMLElement>('.facets__panel-content');
          if (content) {
            content.dataset.lsOwner = details.id;
            if (content.parentElement !== row2) row2.appendChild(content);
          }
        }
      });

      // Return to its <details> any panel left in Row 2 that is no longer the open one.
      row2.querySelectorAll<HTMLElement>('.facets__panel-content').forEach((content) => {
        const ownerId = content.dataset.lsOwner;
        if (ownerId !== openId) {
          const owner = ownerId ? root.querySelector<HTMLElement>(`#${CSS.escape(ownerId)}`) : null;
          if (owner) owner.appendChild(content);
          else content.remove();
        }
      });

      row2.toggleAttribute('data-open', row2.querySelector('.facets__panel-content') != null);
    } finally {
      busy = false;
    }
  };

  const observer = new MutationObserver(() => reconcile());
  observer.observe(root, { childList: true, subtree: true });
  reconcile();

  return {
    unload: () => {
      observer.disconnect();
      const row2 = root.querySelector<HTMLElement>('.ls-facets-row2');
      if (!row2) return;
      row2.querySelectorAll<HTMLElement>('.facets__panel-content').forEach((content) => {
        const owner = content.dataset.lsOwner ? root.querySelector<HTMLElement>(`#${CSS.escape(content.dataset.lsOwner)}`) : null;
        if (owner) owner.appendChild(content);
      });
      row2.remove();
    },
  };
});
